import { Shield, Lock, FileCheck, Eye, Server, UserCheck, Award, Key } from 'lucide-react';

const Security = () => {
  const securityFeatures = [
    {
      icon: Lock,
      title: "AES-256 Encryption",
      description: "All personal data is encrypted at rest using military-grade AES-256 encryption",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: Shield,
      title: "FERPA Compliant",
      description: "Fully compliant with FERPA regulations for educational data protection",
      gradient: "from-cyan-500 to-teal-500"
    },
    {
      icon: FileCheck,
      title: "GDPR & CCPA Ready",
      description: "Meets international privacy standards with comprehensive data controls",
      gradient: "from-teal-500 to-emerald-500"
    },
    {
      icon: Eye,
      title: "Audit Logging",
      description: "Complete audit trails with 12+ months retention for compliance",
      gradient: "from-emerald-500 to-green-500"
    },
    {
      icon: Server,
      title: "Secure Infrastructure",
      description: "Hosted on enterprise-grade cloud infrastructure with 99.9% uptime",
      gradient: "from-blue-500 to-indigo-500"
    },
    {
      icon: UserCheck,
      title: "Access Control",
      description: "Role-based permissions and multi-factor authentication available",
      gradient: "from-indigo-500 to-purple-500"
    }
  ];

  const certifications = [
    { name: "AES-256", icon: Lock, description: "Military-Grade Encryption" },
    { name: "OAuth 2.0", icon: Key, description: "Secure Authentication" },
    { name: "HTTPS", icon: Lock, description: "TLS 1.2+ Encryption" }
  ];

  return (
    <section id="security" className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        />
        
        {/* Floating security shield */}
        <div className="absolute top-20 right-20 opacity-5">
          <Shield className="w-96 h-96 text-cyan-400" />
        </div>
        
        {/* Gradient orbs */}
        <div className="absolute -top-40 -left-40 w-80 h-80 opacity-20">
          <div 
            className="w-full h-full rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(6, 182, 212, 0.3) 0%, transparent 70%)',
              filter: 'blur(80px)'
            }}
          />
        </div>
        
        <div className="absolute -bottom-40 -right-40 w-80 h-80 opacity-20">
          <div 
            className="w-full h-full rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, transparent 70%)',
              filter: 'blur(80px)'
            }}
          />
        </div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-2xl mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Enterprise-Grade{' '}
            <span className="bg-gradient-to-r from-cyan-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
              Security
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Your data security is our top priority. Bloom is built with comprehensive security measures to protect sensitive student-athlete information.
          </p>
        </div>

        {/* Security features grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {securityFeatures.map((feature, index) => (
            <div 
              key={index}
              className="group relative bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700 hover:border-cyan-500/50 transition-all duration-300"
            >
              {/* Hover glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-teal-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              <div className="relative z-10">
                <div className={`w-12 h-12 bg-gradient-to-r ${feature.gradient} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                
                <h3 className="text-lg font-semibold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-400 text-sm">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Standards */}
        <div className="bg-gray-800/30 backdrop-blur-sm rounded-3xl p-8 border border-gray-700">
          <h3 className="text-2xl font-bold text-white text-center mb-8">
            Standards
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {certifications.map((cert, index) => (
              <div key={index} className="text-center group">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-700/50 rounded-2xl mb-4 group-hover:bg-gray-700 transition-colors duration-300">
                  <cert.icon className="w-8 h-8 text-cyan-400" />
                </div>
                <h4 className="text-lg font-semibold text-white mb-1">{cert.name}</h4>
                <p className="text-sm text-gray-400">{cert.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Security;